import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Role, NavigationItem, NAVIGATION_ITEMS, ROLE_HIERARCHY, INSTITUTION_SCOPED_ROLES, HIDDEN_ASSIGNABLE_ROLES } from '../Models/roles.model';

@Injectable({
  providedIn: 'root'
})
export class RolesService {
  private rolesSubject = new BehaviorSubject<Role[]>([]);
  public roles$: Observable<Role[]> = this.rolesSubject.asObservable();

  constructor() {
    this.initializeFromStorage();
  }

  public setRoles(roles: Role[]): void {
    this.rolesSubject.next(roles);
  }

  public getRoles(): Observable<Role[]> {
    return this.roles$;
  }

  public getRoleNames(): string[] {
    return this.rolesSubject.value.map(role => role.roleName);
  }

  public hasRole(roleName: string): boolean {
    return this.getRoleNames().includes(roleName);
  }

  public hasAnyRole(roleNames: readonly string[]): boolean {
    const userRoles = this.getRoleNames();
    return roleNames.some(role => userRoles.includes(role));
  }

  public clearRoles(): void {
    this.rolesSubject.next([]);
  }

  /**
   * Get navigation items that the current user can access based on their roles
   */
  public getAvailableNavigationItems(): NavigationItem[] {
    const userRoles = this.getRoleNames();
    return NAVIGATION_ITEMS.filter(item => 
      item.requiredRoles.some(role => userRoles.includes(role))
    );
  }

  /**
   * Check if user can access a specific navigation path
   */
  public canAccessPath(path: string): boolean {
    const navItem = NAVIGATION_ITEMS.find(item => item.path === path);
    if (!navItem) return false;
    
    return this.hasAnyRole(navItem.requiredRoles);
  }

  /**
   * Get navigation items observable for reactive components
   */
  public getAvailableNavigationItems$(): Observable<NavigationItem[]> {
    return this.roles$.pipe(
      map(() => this.getAvailableNavigationItems())
    );
  }

  /**
   * Feature-specific permission methods
   * Based on User Roles & Permissions Matrix
   */
  public canAddEditInstitutions(): boolean {
    return this.hasAnyRole(['superAdmin', 'salesops']);
  }

  public canGenerateAccessCodes(): boolean {
    return this.hasAnyRole(['superAdmin', 'salesops']);
  }

  public canViewUsers(): boolean {
    return this.hasAnyRole(['superAdmin', 'admin', 'institutionAdmin', 'institutionAdminAdvanced']);
  }

  public canManageUsers(): boolean {
    return this.hasAnyRole(['superAdmin', 'admin']);
  }

  /**
   * Get the highest role level (lowest number) for the current user
   */
  public getHighestRoleLevel(): number {
    const userRoles = this.getRoleNames();
    const userLevels = userRoles
      .map(role => ROLE_HIERARCHY[role])
      .filter(level => level !== undefined);
    
    return userLevels.length > 0 ? Math.min(...userLevels) : 999; // 999 = no valid roles
  }

  /**
   * Get roles that the current user can assign to others
   * Users can only assign roles at or below their own level
   * Excludes roles that are deprecated or not yet implemented
   */
  public getAssignableRoles(): string[] {
    const userLevel = this.getHighestRoleLevel();
    
    return Object.entries(ROLE_HIERARCHY)
      .filter(([roleName, level]) => level >= userLevel) // Can assign same level or lower
      .filter(([roleName]) => !HIDDEN_ASSIGNABLE_ROLES.includes(roleName)) // Exclude hidden roles
      .map(([roleName]) => roleName)
      .sort((a, b) => ROLE_HIERARCHY[a] - ROLE_HIERARCHY[b]); // Sort by hierarchy level
  }

  /**
   * Check if current user can assign a specific role
   */
  public canAssignRole(roleName: string): boolean {
    const userLevel = this.getHighestRoleLevel();
    const roleLevel = ROLE_HIERARCHY[roleName];
    
    return roleLevel !== undefined && roleLevel >= userLevel;
  }

  /**
   * Check if a role requires institution assignment
   */
  public isInstitutionScopedRole(roleName: string): boolean {
    return INSTITUTION_SCOPED_ROLES.includes(roleName);
  }

  /**
   * Get user-friendly display name for a role
   */
  public getRoleDisplayName(roleName: string): string {
    const displayNames: { [key: string]: string } = {
      'superAdmin': 'Super Administrator',
      'admin': 'Administrator', 
      'salesops': 'Sales Operations',
      'integrationAdmin': 'Integration Administrator',
      'institutionAdmin': 'Institution Administrator',
      'institutionAdminAdvanced': 'Advanced Institution Administrator',
      'facilitator': 'Facilitator',
      'participant': 'Participant'
    };
    
    return displayNames[roleName] || roleName;
  }

  private initializeFromStorage(): void {
    // No longer using session storage - roles stay only in memory
  }

}
